// filepath: src/components/inventory/InventoryTable.js
import React, { useState } from "react";
import { useAuth } from '../../../hooks/useAuth';
import EmptyState from '../../CommonComponents/EmptyState';
import { Package, Warehouse } from 'lucide-react';
import ProductImageFallback from '../../CommonComponents/ProductImageFallback';
import TableActionButtons from '../../CommonComponents/TableActionButtons';
import EditStockModal from "./EditStockModal";
import AddStockModal from "./AddStockModal";
import { PackagePlus, Send } from "lucide-react";

// Helper component for image fallback
const InventoryImage = ({ image_url, name, uploadsBaseURL, onPreview }) => {
    const [src, setSrc] = React.useState(image_url ? `${uploadsBaseURL}/${image_url}` : null);

    return (
        <button
            type="button"
            onClick={onPreview}
            className="relative h-9 w-9 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:scale-[1.03] focus:outline-none"
            aria-label={`Preview ${name} image`}
        >
            {src ? (
                <img
                    src={src}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={() => setSrc(null)}
                />
            ) : (
                <ProductImageFallback className="h-full w-full rounded-2xl" aria-label="No product image" />
            )}
        </button>
    );
};

export default function InventoryTable({
    inventories = [],
    onAdjust,
    uploadsBaseURL,
    onToggleActive,
    onEditStock,
    selectedBranchId = "",
    hideActions = false,
    onPreviewImage,
}) {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const { user } = useAuth();
    const isManager = user && user.user_type === 2 && String(user.role_name || '').toLowerCase() === 'manager';
    const addActionLabel = selectedBranchId ? "Release Stock" : "Add Stock";
    const AddActionIcon = selectedBranchId ? Send : PackagePlus;
    const availableColumnLabel = selectedBranchId ? "Warehouse Available" : "Stocks Available";
    const releasedColumnLabel = selectedBranchId ? "Stocks Released" : "Total Released";

    // Handler for edit icon
    const handleEdit = (inventory) => {
        setSelectedInventory(inventory);
        setEditModalOpen(true);
    };

    const handleAddStock = (inventory) => {
        setSelectedInventory(inventory);
        setAddModalOpen(true);
    };


    // Handler for modal save
    const handleSaveStock = (newQuantity) => {
        if (onEditStock && selectedInventory) {
            onEditStock(selectedInventory, newQuantity);
        }
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    // Handler for modal close
    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleCloseAddModal = () => {
        setAddModalOpen(false);
        setSelectedInventory(null);
    };

    const handleSaveAddStock = (addQuantity) => {
        if (onEditStock && selectedInventory) {
            const currentStock = selectedBranchId
                ? (Number(selectedInventory.released_quantity) || 0)
                : (Number(selectedInventory.quantity) || 0);
            const newQuantity = currentStock + (Number(addQuantity) || 0);
            onEditStock(selectedInventory, newQuantity);
        }
        setAddModalOpen(false);
        setSelectedInventory(null);
    };

    return (
        <>
            <div className="overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
                <table className="min-w-[860px] w-full border-collapse">
                    <thead
                        className="border-b"
                        style={{
                            background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                            borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                        }}
                    >
                        <tr>
                            <th className="px-6 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">Item</th>
                            <th className="px-6 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">Unit</th>
                            <th className="px-6 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">{availableColumnLabel}</th>
                            <th className="px-6 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">{releasedColumnLabel}</th>
                            {!hideActions && (
                                <th className="px-6 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {inventories.length === 0 ? (
                            <tr>
                                <td colSpan={hideActions ? 4 : 5} className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <EmptyState icon={Package} message="No inventory found." />
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            inventories.map(inv => (
                                <tr key={inv.id} className="border-b border-slate-100 transition hover:bg-slate-50/70 last:border-0">
                                    <td className="px-6 py-2.5">
                                        <div className="flex items-center gap-2.5">
                                            <InventoryImage
                                                image_url={inv.image_url}
                                                name={inv.product_name}
                                                uploadsBaseURL={uploadsBaseURL}
                                                onPreview={() => onPreviewImage?.(inv)}
                                            />
                                            <div className="min-w-0">
                                                <p className="truncate text-[13px] font-semibold leading-5 text-slate-900">{inv.product_name}</p>
                                                <p className="max-w-[260px] truncate text-[11px] leading-4 text-slate-500" title={inv.description || inv.product_description || ""}>
                                                    {inv.description || inv.product_description || "No description available"}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-2.5 text-center">
                                        <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-600 shadow-sm">
                                            {inv.unit_type || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-2.5 text-center">
                                        <div className="inline-flex min-w-[82px] items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                            <Warehouse className="mr-1.5 h-3.5 w-3.5" />
                                            {inv.quantity}
                                        </div>
                                    </td>
                                    <td className="px-6 py-2.5 text-center">
                                        <div className="inline-flex min-w-[82px] items-center justify-center rounded-2xl border border-amber-100 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                                            {inv.released_quantity ?? 0}
                                        </div>
                                    </td>
                                    {!hideActions && (
                                        <td className="px-6 py-2.5 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <TableActionButtons
                                                    canEdit={!isManager}
                                                    canToggle={false}
                                                    canDelete={false}
                                                    onEdit={() => handleEdit(inv)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddStock(inv)}
                                                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-xl text-white shadow-sm transition hover:scale-[1.03]"
                                                    style={{
                                                        background: 'linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))',
                                                    }}
                                                    title={addActionLabel}
                                                    aria-label={`${addActionLabel} for ${inv.product_name}`}
                                                >
                                                    <AddActionIcon size={16} color="#fff" />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <EditStockModal
                isOpen={editModalOpen}
                onClose={handleCloseModal}
                inventory={selectedInventory}
                onSave={handleSaveStock}
                selectedBranchId={selectedBranchId}
            />
            <AddStockModal
                isOpen={addModalOpen}
                onClose={handleCloseAddModal}
                inventory={selectedInventory}
                onSave={handleSaveAddStock}
                selectedBranchId={selectedBranchId}
            />
        </>
    );
}
